import React, { useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Table,
  Progress,
  Badge,
  DatePicker,
  Select,
  Typography,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  FileTextOutlined,
  FilePdfOutlined,
  FileProtectOutlined,
  PhoneOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
  RightOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  CartesianGrid,
} from 'recharts'

import { leadStore } from '../Lead/leadStore'
import { proposalStore, type ProposalRow } from '../Proposal/proposalStore'
import { agreementStore } from '../Agreement/agreementStore'
import { LeadStage, ProposalStatus, AgreementStatus } from '@/types'
import { useMasterData } from '@/context/MasterDataContext'

const { Title, Text } = Typography

const STAGE_COLORS: Record<string, string> = {
  New: '#3b82f6',
  Contacted: '#06b6d4',
  'Site Visit': '#8b5cf6',
  Negotiation: '#f59e0b',
  Won: '#10b981',
  Lost: '#ef4444',
}

const isDateInPeriod = (
  dateVal: any,
  period: string,
  range: [Dayjs | null, Dayjs | null] | null
): boolean => {
  if (period === 'all') return true
  if (!dateVal) return true
  const d = dayjs(dateVal)
  if (!d.isValid()) return true

  const now = dayjs()
  if (period === '30d') {
    return d.isAfter(now.subtract(30, 'day').startOf('day')) || d.isSame(now, 'month')
  }
  if (period === '90d') {
    return d.isAfter(now.subtract(90, 'day').startOf('day'))
  }
  if (period === 'fy') {
    const currentYear = now.year()
    const fyStart = now.month() >= 3 ? dayjs(`${currentYear}-04-01`) : dayjs(`${currentYear - 1}-04-01`)
    const fyEnd = fyStart.add(1, 'year').subtract(1, 'day').endOf('day')
    return (d.isAfter(fyStart.startOf('day')) || d.isSame(fyStart.startOf('day'))) && (d.isBefore(fyEnd) || d.isSame(fyEnd))
  }
  if (period === 'custom') {
    if (!range || !range[0] || !range[1]) return true
    return (
      (d.isAfter(range[0].startOf('day')) || d.isSame(range[0].startOf('day'))) &&
      (d.isBefore(range[1].endOf('day')) || d.isSame(range[1].endOf('day')))
    )
  }
  return true
}

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { employees } = useMasterData()
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d')
  const [customDateRange, setCustomDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  // Live state from stores
  const [leads, setLeads] = useState(() => leadStore.getLeads())
  const [proposals, setProposals] = useState(() => proposalStore.getAll())
  const [agreements, setAgreements] = useState(() => agreementStore.getAll())

  useEffect(() => {
    const sync = () => {
      setLeads(leadStore.getLeads())
      setProposals(proposalStore.getAll())
      setAgreements(agreementStore.getAll())
    }
    sync()
    leadStore.refresh().then(sync)
    proposalStore.refresh().then(sync)
    agreementStore.refresh().then(sync)

    window.addEventListener(leadStore.eventName, sync)
    window.addEventListener(proposalStore.eventName, sync)
    window.addEventListener(agreementStore.eventName, sync)

    return () => {
      window.removeEventListener(leadStore.eventName, sync)
      window.removeEventListener(proposalStore.eventName, sync)
      window.removeEventListener(agreementStore.eventName, sync)
    }
  }, [])

  // DCR activities derived directly from lead discussion history
  const dcrs = leads.flatMap((l) =>
    (l.discussionHistory ?? []).map((d) => ({
      id: d.id,
      leadId: l.id,
      customerName: l.companyName,
      customerStatus: l.stage,
      discussionPoints: (d as any).notes || d.content || (d as any).discussionPoints || '',
      date: d.createdAt,
      type: d.type,
    }))
  )

  // Filtered arrays based on date period
  const filteredLeads = leads.filter((l) =>
    isDateInPeriod(l.createdAt || (l as any).leadDate || (l as any).date, selectedPeriod, customDateRange)
  )
  const filteredProposals = proposals.filter((p) =>
    isDateInPeriod((p as any).proposalDate || p.createdAt || (p as any).date, selectedPeriod, customDateRange)
  )
  const filteredAgreements = agreements.filter((a) =>
    isDateInPeriod((a as any).agreementDate || a.createdAt || (a as any).date, selectedPeriod, customDateRange)
  )
  const filteredDcrs = dcrs.filter((d) =>
    isDateInPeriod(d.date || (d as any).createdAt, selectedPeriod, customDateRange)
  )

  // Metrics calculation
  const totalLeads = filteredLeads.length
  const consumerLeads = filteredLeads.filter((l) => l.leadType === 'Consumer').length
  const generatorLeads = filteredLeads.filter((l) => l.leadType === 'Generator').length
  const wonLeads = filteredLeads.filter((l) => l.stage === LeadStage.WON).length
  const conversionRate = totalLeads ? Math.round((wonLeads / totalLeads) * 100) : 0

  const totalProposals = filteredProposals.length
  const approvedProposals = filteredProposals.filter((p) => p.status === ProposalStatus.APPROVED).length
  const pendingProposals = filteredProposals.filter(
    (p) =>
      p.status === ProposalStatus.DRAFT ||
      p.status === ProposalStatus.SUBMITTED ||
      p.status === ProposalStatus.UNDER_REVIEW
  ).length

  const activeAgreements = filteredAgreements.filter(
    (a) => a.status === AgreementStatus.ACTIVE || a.status === AgreementStatus.SIGNED
  ).length
  const pendingAgreements = filteredAgreements.filter(
    (a) => a.status === AgreementStatus.READY_FOR_SIGNING || a.status === AgreementStatus.PENDING_SCAN
  ).length

  // Funnel chart data
  const stageCounts: Record<string, number> = {
    New: 0,
    Contacted: 0,
    'Site Visit': 0,
    Negotiation: 0,
    Won: 0,
    Lost: 0,
  }
  filteredLeads.forEach((l) => {
    const stage = l.stage || 'New'
    stageCounts[stage] = (stageCounts[stage] || 0) + 1
  })
  const funnelData = Object.entries(stageCounts).map(([stage, count]) => ({
    stage,
    count,
    fill: STAGE_COLORS[stage] || '#64748b',
  }))

  // Proposal distribution data
  const proposalStatusData = [
    { name: 'Approved', value: approvedProposals, color: '#10b981' },
    { name: 'Pending Review', value: pendingProposals, color: '#f59e0b' },
    { name: 'Won Deals', value: filteredProposals.filter((p) => p.status === ProposalStatus.WON).length, color: '#06b6d4' },
    { name: 'Rejected / Lost', value: filteredProposals.filter((p) => p.status === ProposalStatus.REJECTED || p.status === ProposalStatus.LOST).length, color: '#ef4444' },
  ].filter((d) => d.value > 0)

  // Recent Proposals Columns
  const proposalColumns = [
    {
      title: 'Proposal #',
      dataIndex: 'proposalNumber',
      key: 'proposalNumber',
      render: (val: string, r: ProposalRow) => (
        <span
          style={{ fontWeight: 700, color: '#001A4D', fontFamily: 'JetBrains Mono, monospace', cursor: 'pointer' }}
          onClick={() => navigate(`/proposals/${r.id}`)}
        >
          {val || r.referenceNumber || r.id}
        </span>
      ),
    },
    {
      title: 'Lead Name',
      dataIndex: 'leadName',
      key: 'leadName',
      render: (text: string, r: ProposalRow) => text || r.leadId,
    },
    {
      title: 'Rate (₹/kWh)',
      key: 'rate',
      render: (_: any, r: ProposalRow) => {
        const val = r.pricingDetails?.[0]?.rate || r.sakthiTariff || r.tariff || 0
        return <strong>₹{Number(val).toFixed(2)}</strong>
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ProposalStatus) => (
        <Tag
          color={
            status === ProposalStatus.APPROVED
              ? 'success'
              : status === ProposalStatus.REJECTED || status === ProposalStatus.LOST
              ? 'error'
              : 'processing'
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, r: ProposalRow) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/proposals/${r.id}`)}
        >
          View
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, color: 'var(--brand-primary)', fontWeight: 700 }}>
            Sales Dashboard
          </Title>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Lead Pipeline, Proposal Conversions, Agreements Execution & Sales DCR Activity
          </Text>
        </div>

        <Space wrap style={{ alignItems: 'center' }}>
          <Select
            value={selectedPeriod}
            onChange={(val) => {
              setSelectedPeriod(val)
              if (val !== 'custom') {
                setCustomDateRange(null)
              }
            }}
            style={{ minWidth: 140 }}
            options={[
              { label: 'This Month', value: '30d' },
              { label: 'This Quarter', value: '90d' },
              { label: 'Financial Year', value: 'fy' },
              { label: 'Custom Range', value: 'custom' },
              { label: 'All Time', value: 'all' },
            ]}
          />
          {selectedPeriod === 'custom' && (
            <DatePicker.RangePicker
              value={customDateRange}
              onChange={(dates) => setCustomDateRange(dates as [Dayjs | null, Dayjs | null])}
              style={{ width: 250 }}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
            />
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/leads/new')}>
            New Lead
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={() => navigate('/proposals/new')}>
            Create Proposal
          </Button>
          <Button icon={<FileProtectOutlined />} onClick={() => navigate('/agreements/new')}>
            New Agreement
          </Button>
        </Space>
      </div>

      {/* Top 4 KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Leads</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>{totalLeads}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Consumer: <strong>{consumerLeads}</strong> | Generator: <strong>{generatorLeads}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileTextOutlined style={{ fontSize: 22, color: '#3b82f6' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Conversion Rate</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{conversionRate}%</div>
                <div style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 600 }}>
                  <ArrowUpOutlined /> {wonLeads} Leads Converted to Customer
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircleOutlined style={{ fontSize: 22, color: '#10b981' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Proposals Generated</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{totalProposals}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Approved: <strong>{approvedProposals}</strong> | Pending: <strong>{pendingProposals}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FilePdfOutlined style={{ fontSize: 22, color: '#f59e0b' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #8b5cf6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Agreements Active</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6' }}>{activeAgreements}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Pending Signing: <strong>{pendingAgreements}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileProtectOutlined style={{ fontSize: 22, color: '#8b5cf6' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Visual Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Lead Stage Funnel Pipeline</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Proposal Status Breakdown</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {proposalStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={proposalStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {proposalStatusData.map((entry, index) => (
                        <Cell key={`cell-p-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Text style={{ color: 'var(--text-muted)' }}>No proposals recorded yet</Text>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginTop: -10 }}>
              {proposalStatusData.map((d) => (
                <span key={d.name} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color }} />
                  {d.name}: <strong>{d.value}</strong>
                </span>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tables Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Recent Proposals & Deals</span>}
            extra={<Button type="link" onClick={() => navigate('/proposals')}>View All ({totalProposals})</Button>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}
          >
            <Table
              dataSource={filteredProposals.slice(0, 5)}
              columns={proposalColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Daily Call Reports (DCR) Activity</span>}
            extra={<Button type="link" onClick={() => navigate('/dcr')}>View DCR ({filteredDcrs.length})</Button>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredDcrs.slice(0, 4).map((dcr) => (
                <div
                  key={dcr.id}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--brand-primary)', fontSize: 13 }}>
                      {dcr.customerName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {dcr.discussionPoints || 'Routine follow-up discussion'}
                    </div>
                  </div>
                  <Tag color={dcr.customerStatus === 'Hot' ? 'red' : dcr.customerStatus === 'Warm' ? 'orange' : 'blue'}>
                    {dcr.customerStatus || 'Active'}
                  </Tag>
                </div>
              ))}
              {filteredDcrs.length === 0 && (
                <Text style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                  No call activities logged yet
                </Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default SalesDashboard
